import React, { useState } from "react";
import { Users, BarChart3, Wallet, Ticket } from "lucide-react";
import { toast } from "sonner";
import { AdminSalesAnalytics } from "../AdminSalesAnalytics";
import { AdminWithdrawalRequests } from "../AdminWithdrawalRequests";
import { AdminCreateCoupon } from "../AdminCreateCoupon";
import { InfluencerTierSelector } from "../tiers/InfluencerTierSelector";
import { InfluencersStatsHeader } from "./InfluencersStatsHeader";
import { InfluencerDataGrid } from "./InfluencerDataGrid";
import { InfluencerDetailDrawer } from "./InfluencerDetailDrawer";
import { AnalyticsCharts } from "./AnalyticsCharts";
import { useInfluencersData, type InfluencerRecord } from "./hooks/useInfluencersData";

type SectionTab = "influencers" | "analytics" | "financeiro" | "cupons";

const SECTION_TABS: { id: SectionTab; label: string; icon: React.ElementType }[] = [
  { id: "influencers", label: "Influencers", icon: Users },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "financeiro", label: "Financeiro", icon: Wallet },
  { id: "cupons", label: "Cupons", icon: Ticket },
];

/**
 * Unified admin workspace for influencer operations. Tab 1 is the management
 * data grid; Analytics adds a decision-making chart layer above the existing
 * analytics component; Financeiro / Cupons embed the existing self-contained
 * admin components unchanged.
 */
export const AdminInfluencersDashboard: React.FC = () => {
  const [section, setSection] = useState<SectionTab>("influencers");
  const { rows, monthlyTotals, stats, loading, error, setTier, setAvatar } = useInfluencersData();

  // Store ids (not snapshots) so dialogs always reflect the latest row state,
  // e.g. right after a tier change.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [tierTargetId, setTierTargetId] = useState<string | null>(null);

  const detailRecord = detailId ? rows.find((r) => r.id === detailId) || null : null;
  const tierTarget = tierTargetId ? rows.find((r) => r.id === tierTargetId) || null : null;

  const handleManageCoupon = (record: InfluencerRecord) => {
    setSection("cupons");
    if (record.coupon) {
      toast.info(`Cupom da influenciadora: ${record.coupon}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/15 p-2">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Influencers</h1>
            <p className="text-sm text-muted-foreground">Gestão, performance e financeiro em um só lugar</p>
          </div>
        </div>
        <InfluencersStatsHeader stats={stats} monthlyTotals={monthlyTotals} loading={loading} />
      </div>

      {/* Section tabs — segmented pill control */}
      <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] p-1">
        {SECTION_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${
              section === id
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-white/[0.06]"
            }`}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Section content */}
      <div key={section} className="tab-content-enter">
        {section === "influencers" ? (
          error ? (
            <p className="text-center py-12 text-muted-foreground">{error}</p>
          ) : (
            <InfluencerDataGrid
              rows={rows}
              loading={loading}
              onOpenDetail={(r) => setDetailId(r.id)}
              onOpenTierSelector={(r) => setTierTargetId(r.id)}
              onManageCoupon={handleManageCoupon}
              onAvatarImported={setAvatar}
            />
          )
        ) : section === "analytics" ? (
          <div className="space-y-6">
            <AnalyticsCharts rows={rows} monthlyTotals={monthlyTotals} loading={loading} />
            <AdminSalesAnalytics />
          </div>
        ) : section === "financeiro" ? (
          <AdminWithdrawalRequests />
        ) : (
          <AdminCreateCoupon />
        )}
      </div>

      {/* Influencer detail drawer */}
      <InfluencerDetailDrawer
        influencer={detailRecord}
        onOpenChange={(open) => !open && setDetailId(null)}
        onChangeTier={(r) => setTierTargetId(r.id)}
        onAvatarImported={setAvatar}
      />

      {/* Tier selector (stacks above the drawer when opened from it) */}
      <InfluencerTierSelector
        influencer={tierTarget ? { id: tierTarget.id, name: tierTarget.name, coupon: tierTarget.coupon, tier_id: tierTarget.tier_id } : null}
        onOpenChange={(open) => !open && setTierTargetId(null)}
        onChanged={(userId, tierId) => setTier(userId, tierId)}
      />
    </div>
  );
};

export default AdminInfluencersDashboard;
